import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { CheckCircle } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const PaymentPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const contractId = searchParams.get('contract_id');
  const comparisonIds = searchParams.get('contract_ids');
  const type = searchParams.get('type'); // 'analysis' or 'comparison'

  const packageInfo = {
    analysis: {
      id: 'analysis',
      name: 'Análisis Individual',
      price: 59.90,
      currency: 'MXN',
      features: [
        'Análisis completo con IA GPT-5.2',
        'Extracción de tasas, CAT y comisiones',
        'Identificación de cláusulas de riesgo',
        'Reportes descargables en PDF',
        'Resumen en lenguaje claro'
      ]
    },
    comparison_2: {
      id: 'comparison_2',
      name: 'Comparación de 2 Contratos',
      price: 99.00,
      currency: 'MXN',
      features: [
        'Compara 2 contratos lado a lado',
        'Recomendación personalizada',
        'Análisis de diferencias clave',
        'Tabla comparativa interactiva',
        'Todos los beneficios del análisis individual'
      ]
    },
    comparison_3: {
      id: 'comparison_3',
      name: 'Comparación de 3 Contratos',
      price: 129.00,
      currency: 'MXN',
      features: [
        'Compara 3 contratos lado a lado',
        'Recomendación personalizada avanzada',
        'Análisis exhaustivo de diferencias',
        'Tabla comparativa completa',
        'Mejor valor por análisis'
      ]
    }
  };

  const currentPackage = packageInfo[type] || packageInfo.analysis;

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const metadata = {};
      if (contractId) metadata.contract_id = contractId;
      if (comparisonIds) metadata.contract_ids = comparisonIds;
      console.log('[PaymentPage] Creating checkout with package:', currentPackage.id, 'metadata:', JSON.stringify(metadata));
      const response = await api.createCheckout(currentPackage.id, metadata);
      console.log('[PaymentPage] Stripe checkout URL received, redirecting...');
      window.location.href = response.url;
    } catch (error) {
      console.error('[PaymentPage] Checkout creation failed:', error?.message || error);
      toast.error('Error al crear sesion de pago');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  if (!user) return null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F0' }}>
      <nav className="bg-white border-b border-border shadow-sm">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-3">
              <img src="/assets/logo.png" alt="contractcheck360" className="h-10 w-auto" />
              <span className="text-xl brand-name" style={{ color: '#1A4D2E' }}>
                contractcheck360
              </span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 md:px-8 max-w-2xl py-12">
        <h2 
          className="text-3xl md:text-4xl font-bold mb-4"
          style={{ fontFamily: 'Merriweather, serif', color: '#1A4D2E' }}
        >
          Completar Pago
        </h2>
        <p className="text-lg mb-8" style={{ color: '#64748B' }}>Pago seguro procesado por Stripe</p>

        <div className="bg-white border-2 border-border shadow-sm rounded-sm p-8" style={{ borderColor: '#1A4D2E' }}>
          <h3 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
            {currentPackage.name}
          </h3>
          <div className="text-5xl font-black mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#1A4D2E' }}>
            ${currentPackage.price} {currentPackage.currency || 'MXN'}
          </div>

          <div className="space-y-3 mb-8">
            {currentPackage.features.map((feature, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#1A4D2E' }} />
                <span>{feature}</span>
              </div>
            ))}
          </div>

          <Button
            onClick={handleCheckout}
            disabled={loading}
            className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-medium"
            style={{ backgroundColor: '#1A4D2E' }}
            data-testid="checkout-btn"
          >
            {loading ? 'Redirigiendo...' : 'Proceder al Pago'}
          </Button>

          <p className="text-xs text-center mt-4" style={{ color: '#64748B' }}>
            Pago seguro procesado por Stripe. No almacenamos tu información de pago.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
